import React, { useState, useEffect } from 'react';
import {
    CircularProgress,
    Divider,
    Grid,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Snackbar,
    Collapse,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
    Email as EmailIcon,
    Refresh as RefreshIcon,
    OpenInNew as OpenInNewIcon,
    Chat as ChatIcon,
    LinkOff as LinkOffIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDAvatar from 'components/MDAvatar';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';

// API functions
import { getThreadFromDatabase } from 'services/api/general/email';
import { initiateGoogleAuth, refreshGoogleToken } from 'services/api/user';
import { updateProject } from 'services/api/project';
import { updateProduction } from 'services/api/production';
import { updateLead } from 'services/api/lead';

/**
 * RelatedEmails componenti, herhangi bir varlığa (proje, lead, vb.) bağlı e-posta threadlerini
 * görüntülemek için kullanılır.
 *
 * @param {Object} props - Component props
 * @param {Object} props.entity - İlişkili varlık (proje, lead vb.)
 * @param {String} props.entityType - Varlık tipi ('project', 'lead' vb.)
 * @param {Array} props.threadIds - İlişkilendirilmiş thread ID'leri (varsayılan olarak entity.relatedEmailThreads kullanılır)
 * @param {String} props.title - Başlık (varsayılan "İlişkili E-postalar")
 * @returns {JSX.Element}
 */
const RelatedEmails = ({ entity, entityType = 'project', threadIds, title = 'İlişkili E-postalar' }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [threadData, setThreadData] = useState({});
    const [authError, setAuthError] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, threadId: null });
    const [authAlert, setAuthAlert] = useState({ open: false, message: '', severity: 'info', action: null });
    // State to track which messages are expanded
    const [expandedMessages, setExpandedMessages] = useState({});

    const queryClient = useQueryClient();

    // İlişkili e-posta threadlerini belirle - props üzerinden gelen veya entity üzerinden al
    const relatedEmailThreads = threadIds || entity?.relatedEmailThreads || [];

    console.log('RelatedEmails Debug:', {
        threadIds,
        entityRelatedEmailThreads: entity?.relatedEmailThreads,
        relatedEmailThreads,
        entityType,
        entity: entity?._id,
    });

    // Google bağlantısını kontrol et ve gerekirse yenile/bağlan
    const checkAndRefreshGoogleAuth = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            console.log('Checking Google Auth status');

            // Hiç token yoksa direkt bağlantıya yönlendir - ama önce kullanıcıya bilgi ver
            if (!user?.googleAuth?.accessToken && !user?.googleAuth?.refreshToken) {
                console.log('No Google Auth tokens found, initiating Google Auth');

                setAuthAlert({
                    open: true,
                    message: 'E-posta hizmetlerini kullanmak için Google hesabınıza bağlanmanız gerekiyor.',
                    severity: 'warning',
                    action: async () => {
                        const response = await initiateGoogleAuth();
                        if (response.data.success && response.data.result.authUrl) {
                            localStorage.setItem('last_auth_reason', 'İlk kez Google hesabına bağlanma');
                            window.location.href = response.data.result.authUrl;
                        }
                    },
                });

                return false;
            }

            // Token var ama süresi dolmuşsa yenilemeyi dene
            if (user?.googleAuth?.expiryDate) {
                const expiryDate = new Date(user.googleAuth.expiryDate);
                const now = new Date();

                console.log('Token Status:', {
                    expiryDate,
                    now,
                    isExpired: expiryDate <= now,
                    refreshToken: user?.googleAuth?.refreshToken,
                });

                // Süresi dolmadıysa geçerli token var demektir
                if (expiryDate > now) {
                    console.log('Token is still valid');
                    return true;
                }

                // Süresi dolmuşsa ve refresh token varsa, yenilemeyi dene
                if (user?.googleAuth?.refreshToken) {
                    console.log('Token expired, attempting to refresh');
                    try {
                        const updatedUser = await refreshGoogleToken(user);
                        if (updatedUser) {
                            console.log('Token refreshed successfully');
                            return true;
                        } else {
                            // Sadece refreshToken'in artık geçersiz olduğu kesin olarak anlaşıldığında yeni auth isteyelim
                            console.log('Token refresh failed, initiating Google Auth');

                            setAuthAlert({
                                open: true,
                                message:
                                    'Google oturum bilgileriniz geçersiz. E-posta hizmetlerini kullanmak için yeniden bağlanmanız gerekiyor.',
                                severity: 'warning',
                                action: async () => {
                                    const response = await initiateGoogleAuth();
                                    if (response.data.success && response.data.result.authUrl) {
                                        localStorage.setItem(
                                            'last_auth_reason',
                                            'Refresh token geçersiz olduğu için yeniden bağlanma'
                                        );
                                        window.location.href = response.data.result.authUrl;
                                    }
                                },
                            });

                            return false;
                        }
                    } catch (error) {
                        // Network hatası veya API hatası durumunda kullanıcıyı hemen yönlendirmeyelim
                        // 400 bad request hatası refresh token'in artık geçersiz olduğunu gösterir
                        console.error('Token refresh error:', error);
                        if (error.response?.status === 400) {
                            setAuthAlert({
                                open: true,
                                message:
                                    'Google bağlantınız geçersiz hale gelmiş. E-posta hizmetlerini kullanmak için yeniden bağlanmanız gerekiyor.',
                                severity: 'warning',
                                action: async () => {
                                    const response = await initiateGoogleAuth();
                                    if (response.data.success && response.data.result.authUrl) {
                                        localStorage.setItem(
                                            'last_auth_reason',
                                            'HTTP 400: Refresh token geçersiz olduğu için yeniden bağlanma'
                                        );
                                        window.location.href = response.data.result.authUrl;
                                    }
                                },
                            });
                            return false;
                        }
                        // Diğer hatalarda (network hatası, geçici sunucu hatası gibi) sadece hata gösterelim
                        // ama kullanıcıyı yeniden authorize etmeye zorlamayalım
                        setAuthAlert({
                            open: true,
                            message: 'Google bağlantısında geçici bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
                            severity: 'error',
                            action: null,
                        });
                        return false;
                    }
                }
            }

            console.log('No valid authentication data found');
            return false;
        } catch (error) {
            console.error('Error checking Google Auth:', error);
            setAuthAlert({
                open: true,
                message: 'Google Auth kontrolü sırasında bir hata oluştu.',
                severity: 'error',
                action: null,
            });
            return false;
        }
    };

    // Component yüklendiğinde Google auth kontrol et
    useEffect(() => {
        checkAndRefreshGoogleAuth();
    }, []);

    // React Query to fetch thread data for each thread ID
    const {
        data: queryData,
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: ['emailThreads', relatedEmailThreads, entityType],
        queryFn: async () => {
            // Google Auth kontrolü yap
            await checkAndRefreshGoogleAuth();

            console.log('Query starting with relatedEmailThreads:', relatedEmailThreads);

            // If there are no related threads, don't fetch anything
            if (relatedEmailThreads.length === 0) {
                console.log('No related email threads found, returning empty object');
                return {};
            }

            // Fetch data for each thread ID and store in an object with threadId as key
            const threadsData = {};
            await Promise.all(
                relatedEmailThreads.map(async (threadId) => {
                    try {
                        console.log('Fetching thread:', threadId);
                        const response = await getThreadFromDatabase(threadId, false);
                        if (response.data?.result) {
                            threadsData[threadId] = response.data.result;
                            console.log('Thread data received for:', threadId, response.data.result);
                        }
                    } catch (error) {
                        console.error(`Error fetching thread ${threadId}:`, error);
                        // API hatası auth kaynaklıysa bağlantıyı yenile
                        if (error.response?.status === 401) {
                            checkAndRefreshGoogleAuth();
                        }
                    }
                })
            );
            console.log('Final threadsData:', threadsData);
            return threadsData;
        },
        enabled: relatedEmailThreads.length > 0, // Query'yi sadece thread'ler varsa çalıştır
        refetchOnWindowFocus: false,
    });

    // Query data'sını threadData state'ine sync et
    useEffect(() => {
        if (queryData) {
            console.log('Setting threadData from queryData:', queryData);
            setThreadData(queryData);
        }
    }, [queryData]);

    // Manual refresh butonu için
    const handleRefresh = async () => {
        await checkAndRefreshGoogleAuth();
        refetch();
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    // Format sender name
    const formatSender = (from) => {
        if (!from) return 'Unknown Sender';
        const match = from.match(/^"?([^"<]+)"?\s*(?:<[^>]*>)?$/);
        return match ? match[1].trim() : from;
    };

    // Get the latest message date from a thread
    const getLatestMessageDate = (thread) => {
        if (!thread || !thread.messages || thread.messages.length === 0) {
            return new Date(0);
        }

        return new Date(Math.max(...thread.messages.map((msg) => new Date(msg.date).getTime())));
    };

    // Sort threads by date (most recent first)
    const sortedThreadIds = Object.keys(threadData).sort((a, b) => {
        const dateA = getLatestMessageDate(threadData[a]);
        const dateB = getLatestMessageDate(threadData[b]);
        return dateB - dateA;
    });

    console.log('Render Debug:', {
        threadData,
        threadDataKeys: Object.keys(threadData),
        sortedThreadIds,
        isLoading,
        isError,
        relatedEmailThreadsLength: relatedEmailThreads.length,
    });

    // Query durumunu izlemek için useEffect
    useEffect(() => {
        console.log('Query state changed:', {
            isLoading,
            isError,
            relatedEmailThreadsLength: relatedEmailThreads.length,
            threadDataKeys: Object.keys(threadData),
        });
    }, [isLoading, isError, relatedEmailThreads.length, threadData]);

    // Thread ilişkisini kaldırmak için useMutation hook'u
    const removeThreadMutation = useMutation({
        mutationFn: ({ entityId, threadId }) => {
            // Entity'nin mevcut relatedEmailThreads dizisinden threadId'yi çıkar
            const updatedThreads = (entity?.relatedEmailThreads || []).filter((id) => id !== threadId);

            // Entity tipine göre doğru API fonksiyonunu çağır
            const entityTypeLower = (entityType || '').toLowerCase().trim();

            console.log('Removing thread relation, entity type:', entityTypeLower, 'entity ID:', entityId);

            if (entityTypeLower === 'project') {
                return updateProject(entityId, { relatedEmailThreads: updatedThreads });
            } else if (entityTypeLower === 'production') {
                return updateProduction(entityId, { relatedEmailThreads: updatedThreads });
            } else if (entityTypeLower === 'lead') {
                return updateLead(entityId, { relatedEmailThreads: updatedThreads });
            }

            return Promise.reject(new Error(`Geçersiz entity tipi: ${entityType}`));
        },
        onSuccess: (response) => {
            console.log('Thread ilişkisi başarıyla kaldırıldı:', response);

            // İlişki kaldırıldıktan sonra entity verisini yenile
            if (entityType === 'project') {
                queryClient.invalidateQueries({ queryKey: ['fetchProject', entity?._id] });
            } else if (entityType === 'production') {
                queryClient.invalidateQueries({ queryKey: ['fetchProduction', entity?._id] });
            } else if (entityType === 'lead') {
                queryClient.invalidateQueries({ queryKey: ['fetchLeads', entity?._id] });
            }

            // Thread listesini güncelle
            queryClient.invalidateQueries({ queryKey: ['emailThreads', relatedEmailThreads, entityType] });

            // Seçili tab'ı sıfırla
            setSelectedTab(0);
        },
        onError: (error) => {
            console.error('Thread ilişkisi kaldırılırken hata oluştu:', error);
            // API hatası auth kaynaklıysa bağlantıyı yenile
            if (error.response?.status === 401) {
                checkAndRefreshGoogleAuth();
            }
        },
    });

    // İlişkiyi kaldırma onay dialogını aç
    const handleOpenConfirmDialog = (threadId) => {
        setConfirmDialog({ open: true, threadId });
    };

    // İlişkiyi kaldırma onay dialogını kapat
    const handleCloseConfirmDialog = () => {
        setConfirmDialog({ open: false, threadId: null });
    };

    // Thread ilişkisini kaldır
    const handleRemoveThread = () => {
        const threadId = confirmDialog.threadId;
        if (threadId && entity?._id) {
            removeThreadMutation.mutate({
                entityId: entity._id,
                threadId,
            });
        }
        handleCloseConfirmDialog();
    };

    // Auth Alert'i kapatma işlevi
    const handleCloseAuthAlert = () => {
        setAuthAlert({ ...authAlert, open: false });
    };

    // Auth Alert'i onaylama işlevi
    const handleConfirmAuthAlert = () => {
        if (authAlert.action) {
            authAlert.action();
        }
        handleCloseAuthAlert();
    };

    // Toggle message expansion
    const toggleMessageExpansion = (messageId, event) => {
        // Prevent event bubbling if clicking on links or buttons inside the message
        if (event) {
            event.stopPropagation();
        }

        setExpandedMessages((prev) => ({
            ...prev,
            [messageId]: !prev[messageId],
        }));
    };

    // Render message preview for a thread
    const renderThreadPreview = (threadId) => {
        const thread = threadData[threadId];
        if (!thread || !thread.messages || thread.messages.length === 0) {
            return (
                <MDTypography variant="button" color="text">
                    No messages
                </MDTypography>
            );
        }

        // Get most recent message
        const sortedMessages = [...thread.messages].sort((a, b) => new Date(b.date) - new Date(a.date));
        const latestMessage = sortedMessages[0];

        // İlişkilendirme bilgisine sahip mesajı bul (kaydedilmiş veya createdBy bilgisi olan)
        const savedMessage = thread.messages.find((msg) => msg.isSaved || msg.createdBy);

        return (
            <MDBox
                key={threadId}
                p={2}
                mb={2}
                borderRadius="lg"
                bgColor="light"
                sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 1 },
                }}
                onClick={() => setSelectedTab(sortedThreadIds.indexOf(threadId) + 1)}
            >
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                    <MDTypography variant="button" fontWeight="medium" textTransform="capitalize">
                        {formatSender(latestMessage.from)}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                        {format(new Date(latestMessage.date), 'dd MMM yyyy HH:mm')}
                    </MDTypography>
                </MDBox>
                <MDTypography variant="button" fontWeight="regular" color="text">
                    {latestMessage.subject}
                </MDTypography>
                <MDBox display="flex" mt={1} alignItems="center">
                    <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="light"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {latestMessage.snippet || 'No preview available'}
                    </MDTypography>
                </MDBox>
                <MDBox display="flex" mt={1} gap={1}>
                    <MDBadge
                        variant="contained"
                        color="info"
                        size="sm"
                        badgeContent={`${thread.messages.length} mesaj`}
                        container
                        icon={<ChatIcon fontSize="small" />}
                    />
                    {latestMessage.attachments?.length > 0 && (
                        <MDBadge
                            variant="contained"
                            color="dark"
                            size="sm"
                            badgeContent={`${latestMessage.attachments.length} ek`}
                            container
                        />
                    )}
                </MDBox>
                {/* İlişkilendirme bilgisi - Kim tarafından, ne zaman - Kaydedilmiş mesaj bilgisini göster */}
                {savedMessage && savedMessage.createdBy && savedMessage.createdAt && (
                    <MDBox mt={1} display="flex" justifyContent="flex-end">
                        <MDTypography variant="caption" color="text" fontStyle="italic">
                            {savedMessage.createdBy.name || savedMessage.createdBy.username} tarafından{' '}
                            {format(new Date(savedMessage.createdAt), 'dd.MM.yyyy')} tarihinde kaydedildi
                        </MDTypography>
                    </MDBox>
                )}
            </MDBox>
        );
    };

    // Render full thread detail
    const renderThreadDetail = (threadId) => {
        const thread = threadData[threadId];
        if (!thread || !thread.messages || thread.messages.length === 0) {
            return (
                <MDTypography variant="button" color="text">
                    Thread not found or empty
                </MDTypography>
            );
        }

        // Sort messages by date (oldest first for conversation view)
        const sortedMessages = [...thread.messages].sort((a, b) => new Date(a.date) - new Date(b.date));

        // İlişkilendirme bilgisine sahip mesajı bul (kaydedilmiş veya createdBy bilgisi olan)
        const savedMessage = thread.messages.find((msg) => msg.isSaved || msg.createdBy);

        return (
            <MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                        {sortedMessages[0].subject}
                    </MDTypography>
                    <MDBox display="flex" gap={1}>
                        <Tooltip
                            title={`${
                                entityType === 'project'
                                    ? 'Projeden'
                                    : entityType === 'production'
                                    ? 'Üretimden'
                                    : "Lead'den"
                            } Bağlantıyı Kaldır`}
                        >
                            <IconButton color="error" size="small" onClick={() => handleOpenConfirmDialog(threadId)}>
                                <LinkOffIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Gmail'de Aç">
                            <IconButton
                                color="dark"
                                size="small"
                                onClick={() =>
                                    window.open(`https://mail.google.com/mail/u/0/#inbox/${threadId}`, '_blank')
                                }
                            >
                                <OpenInNewIcon />
                            </IconButton>
                        </Tooltip>
                    </MDBox>
                </MDBox>
                {/* İlişkilendirme bilgisi - Kim tarafından, ne zaman */}
                {savedMessage && savedMessage.createdBy && savedMessage.createdAt && (
                    <MDBox mb={2} display="flex" alignItems="center">
                        <MDAvatar
                            size="xs"
                            bgColor="dark"
                            name={savedMessage.createdBy.name || savedMessage.createdBy.username}
                            src={savedMessage.createdBy.profilePicture}
                            sx={{ mr: 1 }}
                        />
                        <MDTypography variant="caption" color="text">
                            {savedMessage.createdBy.name || savedMessage.createdBy.username} tarafından{' '}
                            {format(new Date(savedMessage.createdAt), 'dd MMM yyyy HH:mm')} tarihinde ilişkilendirildi
                            {savedMessage.isSaved && (
                                <MDBadge
                                    variant="contained"
                                    color="success"
                                    size="xs"
                                    badgeContent="Kaydedildi"
                                    container
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </MDTypography>
                    </MDBox>
                )}
                <Divider />
                <MDBox mt={3}>
                    <MDTypography variant="button" color="text" fontWeight="medium" mb={2} display="block">
                        E-posta Dizisi ({sortedMessages.length} mesaj)
                    </MDTypography>

                    {sortedMessages.map((message, index) => (
                        <MDBox
                            key={message.id}
                            p={2}
                            pl={3}
                            mb={index < sortedMessages.length - 1 ? 2 : 0}
                            borderRadius="lg"
                            bgcolor="grey.100"
                            sx={{
                                border: '1px solid',
                                borderColor: 'grey.300',
                                position: 'relative',
                                '&:hover': {
                                    boxShadow: 1,
                                },
                                cursor: 'pointer',
                            }}
                            onClick={(e) => toggleMessageExpansion(message.id, e)}
                        >
                            {/* Timeline dot */}
                            <MDBox
                                position="absolute"
                                left={0}
                                top="50%"
                                sx={{
                                    transform: 'translate(-50%, -50%)',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: 'info.main',
                                    zIndex: 2,
                                    border: '2px solid #fff',
                                }}
                            />

                            {/* Message header */}
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <MDBox display="flex" alignItems="center">
                                    <MDAvatar
                                        size="sm"
                                        bgColor="dark"
                                        name={formatSender(message.from)}
                                        sx={{ mr: 1 }}
                                    />
                                    <MDBox>
                                        <MDTypography variant="button" fontWeight="medium">
                                            {formatSender(message.from)}
                                        </MDTypography>
                                        <MDTypography variant="caption" color="text" display="block">
                                            Alıcı: {message.to || 'N/A'}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                                <MDBox display="flex" alignItems="center">
                                    <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>
                                        {format(new Date(message.date), 'dd MMM yyyy HH:mm')}
                                    </MDTypography>
                                    {expandedMessages[message.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </MDBox>
                            </MDBox>

                            {/* Message snippet (always visible) */}
                            <MDBox mt={1}>
                                <MDTypography variant="body2" color="text">
                                    {message.snippet || 'İçerik bulunamadı'}
                                </MDTypography>
                            </MDBox>

                            {/* Message full content (collapsible) */}
                            <Collapse in={expandedMessages[message.id]} timeout="auto" unmountOnExit>
                                <MDBox
                                    mt={2}
                                    borderTop="1px dashed"
                                    borderColor="grey.300"
                                    pt={2}
                                    sx={{
                                        '& a': { color: 'dark.main' },
                                        '& img': { maxWidth: '100%', height: 'auto' },
                                    }}
                                >
                                    {message.body ? (
                                        <div
                                            dangerouslySetInnerHTML={{ __html: message.body }}
                                            style={{
                                                wordBreak: 'break-word',
                                                overflow: 'auto',
                                                maxHeight: '300px',
                                            }}
                                        />
                                    ) : (
                                        <MDTypography variant="body2" color="text" fontStyle="italic">
                                            İçerik bulunamadı
                                        </MDTypography>
                                    )}
                                </MDBox>

                                {/* Attachments if any */}
                                {message.attachments && message.attachments.length > 0 && (
                                    <MDBox mt={2} p={1.5} bgColor="grey.200" borderRadius="md">
                                        <MDTypography variant="button" color="text" fontWeight="medium" mb={1}>
                                            Ekler ({message.attachments.length})
                                        </MDTypography>
                                        <Grid container spacing={1}>
                                            {message.attachments.map((attachment) => (
                                                <Grid item key={attachment.filename} xs={12} sm={6} md={4}>
                                                    <MDBox
                                                        display="flex"
                                                        alignItems="center"
                                                        p={1}
                                                        borderRadius="md"
                                                        bgColor="white"
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <EmailIcon sx={{ mr: 1 }} />
                                                        <MDTypography
                                                            variant="button"
                                                            fontWeight="regular"
                                                            sx={{
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {attachment.filename}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </MDBox>
                                )}
                            </Collapse>
                        </MDBox>
                    ))}
                </MDBox>
            </MDBox>
        );
    };

    return (
        <MDBox p={3} borderRadius="lg" bgColor="white" shadow="lg">
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pb={2}
                mb={3}
                borderBottom="1px solid"
                borderColor="grey.300"
            >
                <MDTypography variant="h5" fontWeight="medium">
                    {title}
                </MDTypography>
                <MDButton
                    color="dark"
                    variant="gradient"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                >
                    Yenile
                </MDButton>
            </MDBox>
            {authError && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {authError}
                </Alert>
            )}
            {isLoading ? (
                <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress />
                </MDBox>
            ) : isError ? (
                <MDBox p={3} textAlign="center">
                    <MDTypography variant="body" color="error">
                        E-postalar yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.
                    </MDTypography>
                </MDBox>
            ) : relatedEmailThreads.length === 0 ? (
                <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="text">
                        İlişkilendirilmiş e-posta bulunamadı.
                    </MDTypography>
                </MDBox>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                mb: 3,
                                borderBottom: 1,
                                borderColor: 'divider',
                                '& .MuiTab-root': {
                                    minWidth: 120,
                                    maxWidth: 180,
                                },
                            }}
                        >
                            <Tab label="Tüm E-postalar" icon={<EmailIcon />} iconPosition="start" />
                            {sortedThreadIds.map((threadId, index) => {
                                const thread = threadData[threadId];
                                if (!thread || !thread.messages || thread.messages.length === 0) return null;

                                const latestMessage = [...thread.messages].sort(
                                    (a, b) => new Date(b.date) - new Date(a.date)
                                )[0];

                                // Limit subject length to prevent UI issues
                                const truncatedSubject =
                                    latestMessage.subject.length > 25
                                        ? latestMessage.subject.substring(0, 22) + '...'
                                        : latestMessage.subject;

                                return (
                                    <Tab
                                        key={threadId}
                                        label={truncatedSubject}
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                    </Grid>

                    <Grid item xs={12}>
                        {selectedTab === 0 ? (
                            <MDBox>
                                {Object.keys(threadData).length === 0 ? (
                                    <MDBox p={3} textAlign="center">
                                        <MDTypography variant="body2" color="text">
                                            E-posta thread'leri yüklenemiyor.
                                        </MDTypography>
                                    </MDBox>
                                ) : (
                                    sortedThreadIds.map((threadId) => renderThreadPreview(threadId))
                                )}
                            </MDBox>
                        ) : (
                            renderThreadDetail(sortedThreadIds[selectedTab - 1])
                        )}
                    </Grid>
                </Grid>
            )}
            {/* İlişki Kaldırma Onay Dialogu */}
            <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Bağlantıyı Kaldır</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Bu e-posta dizisinin{' '}
                        {entityType === 'project' ? 'proje' : entityType === 'production' ? 'üretim' : 'lead'} ile olan
                        bağlantısını kaldırmak istediğinizden emin misiniz? Bu işlem e-postaları silmez, sadece{' '}
                        {entityType === 'project' ? 'proje' : entityType === 'production' ? 'üretim' : 'lead'} ile olan
                        bağlantıyı kaldırır.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ pb: 2, px: 3 }}>
                    <MDButton onClick={handleCloseConfirmDialog} color="dark" variant="outlined">
                        İptal
                    </MDButton>
                    <MDButton
                        onClick={handleRemoveThread}
                        color="error"
                        variant="contained"
                        disabled={removeThreadMutation.isLoading}
                    >
                        {removeThreadMutation.isLoading ? 'İşleniyor...' : 'Bağlantıyı Kaldır'}
                    </MDButton>
                </DialogActions>
            </Dialog>
            {/* Auth Alert Dialogs */}
            <Dialog open={authAlert.open && authAlert.action !== null} onClose={handleCloseAuthAlert}>
                <DialogTitle>Google Hesap Bağlantısı Gerekli</DialogTitle>
                <DialogContent>
                    <DialogContentText>{authAlert.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCloseAuthAlert} color="secondary">
                        İptal
                    </MDButton>
                    <MDButton onClick={handleConfirmAuthAlert} color="primary">
                        Google'a Bağlan
                    </MDButton>
                </DialogActions>
            </Dialog>
            {/* Bilgi/Hata bildirimleri için Snackbar */}
            <Snackbar
                open={authAlert.open && authAlert.action === null}
                autoHideDuration={6000}
                onClose={handleCloseAuthAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseAuthAlert} severity={authAlert.severity}>
                    {authAlert.message}
                </Alert>
            </Snackbar>
        </MDBox>
    );
};

export default RelatedEmails;
